import moment from 'moment';
import { find, get } from 'lodash';
import BigNumber from 'bignumber.js';
import {
  CouponLifeDateTypeMap,
  CouponLifeTimeType,
  CouponRuleCodeMap,
  CouponUseChannelOption,
  CouponUseChannelType,
  CouponUseShopRange,
  CouponUseShopRangeMap,
  DATE_FORMAT,
  OCC_TYPE_LIST,
} from '@/config';
import { renderIntlLabel } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';

const parseFen2Yuan = (val) => new BigNumber(val).dividedBy(100).toNumber();

// 是否直接抵扣金额
export const isDeductByValue = (couponSubType) => {
  // 10101 电酒住宿直抵券、10102 电酒住宿满额抵扣券
  return couponSubType === '10101' || couponSubType === '10102';
};

export const CouponDescriptionGetter = {
  // 优惠券类型
  couponTypeDesc: (detail) => {
    if (isDeductByValue(detail.couponSubType))
      return renderIntlLabel('guest_coupon_label_reduct_room_price_coupon');
    return renderIntlLabel('guest_coupon_label_reduct_room_price_coupon');
  },
  // 优惠券面额
  faceValueDesc: (detail) => {
    const ruleList = get(detail, 'ruleSet.ruleList');
    // 使用门槛
    const limitVal = parseFen2Yuan(
      get(find(ruleList, { code: CouponRuleCodeMap.deductStartCode }), 'valueList.0'),
    );
    const isNoLimit = !limitVal;
    if (isDeductByValue(detail.couponSubType)) {
      // 满减
      const deductVal = parseFen2Yuan(
        get(find(ruleList, { code: CouponRuleCodeMap.deductAmountCode }), 'valueList.0'),
      );
      return isNoLimit
        ? renderIntlLabel('guest_coupon_label_deductVal', { deductVal })
        : renderIntlLabel('guest_coupon_label_reach_limitVal_deductVal', { limitVal, deductVal });
    }
    // 满折
    const percentRule = find(ruleList, { code: CouponRuleCodeMap.deductPercentCode });
    const percentVal = new BigNumber(get(percentRule, 'valueList.0')).dividedBy(1000).toNumber();
    const maxDeductVal = parseFen2Yuan(get(percentRule, 'valueList.1'));
    return isNoLimit
      ? renderIntlLabel('guest_coupon_label_has_discount_immediately', { percentVal, maxDeductVal })
      : renderIntlLabel('guest_coupon_label_reach_limitVal_discountVal', {
          limitVal,
          percentVal,
          maxDeductVal,
        });
  },
  // 基础有效期
  baseLifeDesc: (detail) => {
    const bindConfigTime = get(detail, 'couponBindConfigTimeResp');
    if (bindConfigTime) {
      const {
        expirationTimeType,
        relativeStartTimeUnit,
        relativeStartTimeValue,
        relativeTimeUnit,
        relativeTimeValue,
        absoluteEndTimestamp,
        absoluteStartTimestamp,
      } = bindConfigTime;
      if (expirationTimeType === CouponLifeTimeType.Absolute) {
        return `${getTimeByUtcOffset(absoluteStartTimestamp, DATE_FORMAT)} ~ ${getTimeByUtcOffset(
          absoluteEndTimestamp,
          DATE_FORMAT,
        )} ${renderIntlLabel('guest_coupon_label_effictive')}`;
      }
      return !relativeStartTimeValue
        ? renderIntlLabel('guest_coupon_label_effective_rule_immediately', {
            relativeTimeValue,
            relativeTimeUnit: CouponLifeDateTypeMap[relativeTimeUnit],
          })
        : renderIntlLabel('guest_coupon_label_effective_rule_laster', {
            relativeStartTimeValue,
            relativeStartTimeUnit: CouponLifeDateTypeMap[relativeStartTimeUnit],
            relativeTimeValue,
            relativeTimeUnit: CouponLifeDateTypeMap[relativeTimeUnit],
          });
      // return `自领取起${
      //   !relativeStartTimeValue
      //     ? '立即'
      //     : `${relativeStartTimeValue}${CouponLifeDateTypeMap[relativeStartTimeUnit]}后`
      // }生效，生效后${relativeTimeValue}${CouponLifeDateTypeMap[relativeTimeUnit]}内有效`;
    }
    return '';
  },
  // 可用日期
  useTimeDesc: (detail) => {
    const ruleList = get(detail, 'ruleSet.ruleList');
    return get(find(ruleList, { code: CouponRuleCodeMap.useDateTypeCode }), 'ruleNote');
  },
  // 可用入住类型
  useOccTypeDesc: (detail) => {
    const ruleList = get(detail, 'ruleSet.ruleList');
    const ruleValue =
      get(find(ruleList, { code: CouponRuleCodeMap.useOccTypeCode }), 'valueList.0') || [];
    const occDescList = [];
    const checkedTypeMap = {};
    ruleValue.forEach((code) => {
      checkedTypeMap[code] = true;
    });
    OCC_TYPE_LIST.forEach((item) => {
      if (checkedTypeMap[item.value]) {
        occDescList.push(item.label);
      }
    });
    if (occDescList.length) {
      return occDescList.length === 1
        ? renderIntlLabel('guest_coupon_label_avalible_shopCode_only_inSomeStore', {
            bindCommonCode: occDescList.join('、'),
          })
        : renderIntlLabel('guest_coupon_label_support_avalible_inSomeStore_use', {
            bindCommonCode: occDescList.join('、'),
          });
      // return `${occDescList.length === 1 ? '仅限' : '支持'}${occDescList.join('、')}使用`;
    }
    return undefined;
  },
  // 可用渠道
  useChannelDesc: (detail) => {
    const ruleList = get(detail, 'ruleSet.ruleList');
    const ruleValue =
      get(find(ruleList, { code: CouponRuleCodeMap.useChannelTypeCode }), 'valueList.0') || [];
    const channelDescMap = {
      [CouponUseChannelType.PMS]: renderIntlLabel('guest_enum_CouponUseChannelType_walkin'),
      [CouponUseChannelType.APP]: renderIntlLabel('guest_enum_CouponUseChannelType_APP'),
      [CouponUseChannelType.WXMiniProgram]: renderIntlLabel(
        'guest_enum_CouponUseChannelType_WXMiniProgram',
      ),
      [CouponUseChannelType.ALIPAYMiniProgram]: renderIntlLabel(
        'guest_enum_CouponUseChannelType_AlipayMiniProgram',
      ),
    };
    const channelDescList = [];
    const checkedChannelMap = {};
    ruleValue.forEach((code) => {
      checkedChannelMap[code] = true;
    });
    CouponUseChannelOption.forEach((item) => {
      if (checkedChannelMap[item.value]) {
        channelDescList.push(channelDescMap[item.value]);
      }
    });
    if (channelDescList.length) {
      return channelDescList.length === 1
        ? renderIntlLabel('guest_coupon_label_avalible_shopCode_only_inSomeStore', {
            bindCommonCode: channelDescList.join('、'),
          })
        : renderIntlLabel('guest_coupon_label_support_avalible_inSomeStore_use', {
            bindCommonCode: channelDescList.join('、'),
          });
      // return `${channelDescList.length === 1 ? '仅限' : '支持'}${channelDescList.join('、')}使用`;
    }
    return undefined;
  },
  // 可用门店
  useStoreDesc: (detail) => {
    const ruleList = get(detail, 'ruleSet.ruleList');
    const ruleValue =
      get(find(ruleList, { code: CouponRuleCodeMap.useStoreTypeCode }), 'valueList.0') || [];
    const couponStoreRangeType = get(ruleValue, 'type') || CouponUseShopRange.AllShop;
    return CouponUseShopRangeMap[couponStoreRangeType];
  },
};
